import FormSection from '@/components/Forms/FormSection';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import usePermissions from '@/hooks/usePermissions';
import { Box, Button, FormikTextInput, SimulatedEventPayloadType, TextInput } from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';

import { UserPermissions, WaterHeaterSitePrepItem } from 'types';

export const SitePrepSection = ({
  systemIndex,
  isInstallPackage = false,
  isPackageEditable = true,
}: {
  systemIndex: number;
  isInstallPackage?: boolean;
  isPackageEditable?: boolean;
}) => {
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const { values, setFieldValue } = useFormikContext<any>();
  return (
    <FormSection
      title="Site Prep"
      description={
        <Box gap="sm" fontSize="xs">
          <Box>List all that apply</Box>
        </Box>
      }
      baseStyle={{
        borderWidth: '0',
      }}
    >
      <FieldArray
        name={`systems.${systemIndex}.sitePrep.items`}
        render={(arrayHelpers) => (
          <Box gap="sm">
            {values?.systems[systemIndex]?.sitePrep?.items?.map(
              (sitePrep: WaterHeaterSitePrepItem, index: number) => (
                <Box
                  key={`system-${systemIndex}-sitePrep-${index}`}
                  direction="row"
                  gap="lg"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box direction="column" gap="sm" width="512px">
                    {isInstallPackage ? (
                      <Box fontSize="md">{sitePrep.name}</Box>
                    ) : (
                      <Field
                        label={`Site Prep ${index + 1}`}
                        name={`systems.${systemIndex}.sitePrep.items.${index}.name`}
                        id={`systems.${systemIndex}.sitePrep.items.${index}.name`}
                        component={FormikTextInput}
                        placeholder="Enter Site Prep Details"
                        isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                        isRequired={true}
                      />
                    )}
                  </Box>
                  <Box width="lg"></Box>
                  <Box direction="column" gap="lg" width="xl" position="relative"></Box>
                  {!isInstallPackage && (
                    <Box padding="lg 0 0 0">
                      <Button
                        as="button"
                        variant="tertiary"
                        tone="danger"
                        isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                        onClick={() => {
                          arrayHelpers.remove(index);
                          if (values?.systems[systemIndex]?.sitePrep?.items?.length === 1) {
                            setFieldValue(`systems.${systemIndex}.sitePrep.totalCost`, 0);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </Box>
              ),
            )}
            {!isInstallPackage && (
              <Box alignItems="flex-start">
                <Button
                  as="button"
                  variant="secondary"
                  size="sm"
                  iconPrefix="add"
                  isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                  onClick={() =>
                    arrayHelpers.push({
                      name: '',
                      quantity: 1,
                    })
                  }
                >
                  Add Site Prep
                </Button>
              </Box>
            )}
            {values?.systems[systemIndex]?.sitePrep?.items?.length ? (
              <Box gap="xl" alignSelf="flex-end">
                {isInstallPackage ? (
                  <Box>
                    <Box fontSize="md" fontWeight="medium">
                      Total Cost
                    </Box>
                    <Box>{currencyFormatter.format(values?.systems[systemIndex]?.sitePrep?.totalCost || 0)}</Box>
                  </Box>
                ) : (
                  <TextInput
                    label="Total Cost"
                    name={`systems.${systemIndex}.sitePrep.totalCost`}
                    id={`systems.${systemIndex}.sitePrep.totalCost`}
                    value={values?.systems[systemIndex]?.sitePrep?.totalCost}
                    type="number"
                    prefix="$"
                    width="xl"
                    isDisabled={userOnlyHasReaderPermission || isInstallPackage || !isPackageEditable}
                    onFocus={(event: SimulatedEventPayloadType) => {
                      const value = parseFloat(event.target.value);
                      if (event.target.value === '0') {
                        setFieldValue(`systems.${systemIndex}.sitePrep.totalCost`, '');
                      } else {
                        setFieldValue(`systems.${systemIndex}.sitePrep.totalCost`, isNaN(value) ? 0 : value);
                      }
                    }}
                    onBlur={(event: SimulatedEventPayloadType) => {
                      const value = parseFloat(event.target.value);
                      setFieldValue(`systems.${systemIndex}.sitePrep.totalCost`, isNaN(value) ? 0 : value);
                    }}
                    onChange={(event: SimulatedEventPayloadType) => {
                      const value = event.target.value;
                      setFieldValue(`systems.${systemIndex}.sitePrep.totalCost`, `${value}`);
                    }}
                  />
                )}
              </Box>
            ) : null}
          </Box>
        )}
      />
    </FormSection>
  );
};
